import { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { isString } from 'lodash';
import Dropdown from '../../Dropdown';
import TwAutocomplete from '../../TwAutocomplete';
import Filter from '../Filter';
import useStyles from './styles';

const SingleSelectAutocompleteFilter = ({
  onChange,
  onInputChange,
  label,
  tooltipPrependText,
  value,
  options,
  variant,
  filterButtonMaxWidth,
  widthInside,
  doHighlightOnChange,
  isOptionsLoading,
  filterSpacingClass,
  menuWidthClass,
  fontSizeStyle,
  bgClassName,
  textColorClassName,
  placeholderText,
}) => {
  const classes = useStyles(fontSizeStyle);
  const getLabel = () => (value ? value.label : label);

  const handleChange = useCallback((newValue) => {
    if (!isString(newValue)) {
      onChange(newValue);
    } else {
      onChange({
        featureClass: '',
        value: newValue,
        label: newValue,
      });
    }
  }, [onChange]);

  const clearFilter = useCallback(() => {
    handleChange(null);
  }, [handleChange]);

  return (
    <Filter
      label={getLabel()}
      tooltipPrependText={tooltipPrependText}
      menuWidthClass={menuWidthClass}
      isFilterEmpty={doHighlightOnChange ? !value : true}
      changeFilter={onChange}
      variant={variant}
      filterMaxWidthClass={filterButtonMaxWidth}
      handleClear={clearFilter}
      widthInside={widthInside}
      filterSpacingClass={filterSpacingClass}
      bgClassName={bgClassName}
      textColorClassName={textColorClassName}
    >
      <Dropdown.Item>
        <div className="col-span-5">
          <TwAutocomplete
            isMultiple={false}
            placeholderText={placeholderText}
            onChange={handleChange}
            classes={classes}
            options={options}
            onInputChange={onInputChange}
            values={value}
            isNested
            disableClearable
            isLoading={isOptionsLoading}
            freeSolo={false}
          />
        </div>
      </Dropdown.Item>
    </Filter>
  );
};

SingleSelectAutocompleteFilter.propTypes = {
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  label: PropTypes.string,
  tooltipPrependText: PropTypes.string,
  value: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  options: PropTypes.arrayOf(PropTypes.shape({
    suggests: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })),
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  variant: PropTypes.string,
  filterButtonMaxWidth: PropTypes.string,
  widthInside: PropTypes.string,
  doHighlightOnChange: PropTypes.bool,
  isOptionsLoading: PropTypes.bool,
  filterSpacingClass: PropTypes.string,
  menuWidthClass: PropTypes.string,
  fontSizeStyle: PropTypes.shape(),
  textColorClassName: PropTypes.string,
  bgClassName: PropTypes.string,
  placeholderText: PropTypes.string,
};

SingleSelectAutocompleteFilter.defaultProps = {
  onInputChange: undefined,
  label: undefined,
  tooltipPrependText: undefined,
  value: undefined,
  options: undefined,
  variant: 'standard',
  filterButtonMaxWidth: 'm-w-filter',
  widthInside: 'max-w-85',
  doHighlightOnChange: true,
  isOptionsLoading: false,
  filterSpacingClass: undefined,
  menuWidthClass: 'w-108',
  fontSizeStyle: undefined,
  textColorClassName: undefined,
  bgClassName: undefined,
  placeholderText: 'Start typing...',
};

export default memo(SingleSelectAutocompleteFilter);
